import { Box, Button, Divider, Grid, Group, Modal, Stack, Title } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { cloneDeep, get, set } from 'lodash';
import { z } from 'zod';

import { Declaration, EnvironmentCountries } from '@e-origin/shared';

import { EucdmCode, ManageSupportingDocumentsRow, ManageSupportingDocumentsTable, TextInput } from '@components';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useSettings } from '@hooks/useSettings';
import { disableAmendmentField, INNER_WHITESPACE_ERROR, noInnerWhitespace, trimEdges, trimOnBlurProps } from '@utils';
import { CommodityCodeRow } from '../beforeSendingBatchFormIntializer';
import classes from './EditCommodityCode.module.css';

interface EditCommodityCodeProps {
  opened: boolean;
  onHide: () => void;
  commodityCode?: CommodityCodeValues;
  onSave: (savedCommodityCode: CommodityCodeRow) => void;
  disabled: boolean;
  declaration?: Declaration;
}

// leading/trailing whitespace is trimmed away, anything left inside the code is rejected
const additionalCodeSchema = z.string().trim().refine(noInnerWhitespace, { message: INNER_WHITESPACE_ERROR }).optional();

const ADDITIONAL_CODE_PATHS = [
  'firstItemData.taricAdditionalCode',
  'firstItemData.taricAdditionalCode2',
  'firstItemData.nationalAdditionalCode',
  'firstItemData.nationalAdditionalCode2',
  'firstItemData.nationalAdditionalCode3',
];

const commodityCodeSchema = z.object({
  firstItemData: z.object({
    hsCode: z.string().optional(),
    taricAdditionalCode: additionalCodeSchema,
    taricAdditionalCode2: additionalCodeSchema,
    nationalAdditionalCode: additionalCodeSchema,
    nationalAdditionalCode2: additionalCodeSchema,
    nationalAdditionalCode3: additionalCodeSchema,
  }),
  additionalReference: z
    .array(
      z.object({
        selected: z.boolean(),
        document: z.object({
          type: z.string().optional(),
          referenceNumber: z.string().optional(),
          dateOfValidity: z.string().optional(),
        }),
      }),
    )
    .optional(),
  riskAnalysis: z
    .object({
      hsCodeFound: z.boolean(),
      taricData: z.record(z.any()).optional(),
    })
    .optional(),
});

export type CommodityCodeValues = z.infer<typeof commodityCodeSchema>;

export const EditCommodityCode: React.FC<EditCommodityCodeProps> = ({ opened, onHide, commodityCode, onSave, disabled, declaration }) => {
  const { data: settings } = useSettings();

  const form = useFormWithNameInputProp<CommodityCodeValues>({
    initialValues: {
      firstItemData: {
        hsCode: commodityCode?.firstItemData?.hsCode ?? '',
        taricAdditionalCode: commodityCode?.firstItemData?.taricAdditionalCode ?? '',
        taricAdditionalCode2: commodityCode?.firstItemData?.taricAdditionalCode2 ?? '',
        nationalAdditionalCode: commodityCode?.firstItemData?.nationalAdditionalCode ?? '',
        nationalAdditionalCode2: commodityCode?.firstItemData?.nationalAdditionalCode2 ?? '',
        nationalAdditionalCode3: commodityCode?.firstItemData?.nationalAdditionalCode3 ?? '',
      },
      additionalReference: (commodityCode?.additionalReference || []).map((ar) => ({
        selected: ar.selected || false,
        document: {
          type: ar.document?.type ?? '',
          referenceNumber: ar.document?.referenceNumber ?? '',
          dateOfValidity: ar.document?.dateOfValidity ?? '',
        },
      })),
      riskAnalysis: commodityCode?.riskAnalysis || { hsCodeFound: false, taricData: {} },
    },
    validate: zodResolver(commodityCodeSchema),
    validateInputOnBlur: true,
  });

  // the additional codes are trimmed as soon as the user leaves the field, so the cleaned value
  // is what gets validated and saved
  const onSubmit = (values: CommodityCodeValues) => {
    const trimmedValues = cloneDeep(values);
    ADDITIONAL_CODE_PATHS.forEach((path) => set(trimmedValues, path, trimEdges(get(trimmedValues, path))));

    onSave(trimmedValues as CommodityCodeRow);
    onHide();
  };

  const handleTableUpdate = (rows: ManageSupportingDocumentsRow[]) => {
    form.setFieldValue('additionalReference', rows);
  };

  return (
    <Modal
      classNames={{ body: classes.modalBody, header: classes.modalHeader }}
      title={`Edit - HS Code ${commodityCode?.firstItemData?.hsCode}`}
      opened={opened}
      onClose={onHide}
      zIndex={9999}
      centered
      size="lg"
    >
      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack gap="md" mt="md">
          <Divider my="sm" />

          <Grid>
            <Grid.Col span={6}>
              <EucdmCode path="firstItemData.taricAdditionalCode">
                <TextInput
                  data-pw="taric-code1-input"
                  label="Taric Additional Code 1"
                  {...trimOnBlurProps(form, 'firstItemData.taricAdditionalCode')}
                  disabled={disabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
                  size="md"
                />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={6}>
              <EucdmCode path="firstItemData.taricAdditionalCode2">
                <TextInput
                  label="Taric Additional Code 2"
                  {...trimOnBlurProps(form, 'firstItemData.taricAdditionalCode2')}
                  disabled={disabled || disableAmendmentField({ declaration, settingsCountry: settings.country, countries: [EnvironmentCountries.BE] })}
                  size="md"
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={6}>
              <EucdmCode path="firstItemData.nationalAdditionalCode">
                <TextInput
                  data-pw="national-code1-input"
                  label="National Additional Code 1"
                  {...trimOnBlurProps(form, 'firstItemData.nationalAdditionalCode')}
                  disabled={disabled}
                  size="md"
                />
              </EucdmCode>
            </Grid.Col>
            <Grid.Col span={6}>
              <EucdmCode path="firstItemData.nationalAdditionalCode2">
                <TextInput
                  label="National Additional Code 2"
                  {...trimOnBlurProps(form, 'firstItemData.nationalAdditionalCode2')}
                  disabled={disabled}
                  size="md"
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={12}>
              <EucdmCode path="firstItemData.nationalAdditionalCode3">
                <TextInput
                  label="National Additional Code 3"
                  {...trimOnBlurProps(form, 'firstItemData.nationalAdditionalCode3')}
                  disabled={disabled}
                  size="md"
                />
              </EucdmCode>
            </Grid.Col>
          </Grid>

          <Box pt={50}>
            <Title order={4} fw={700} mb="md">
              Additional Reference Documents
            </Title>
            <ManageSupportingDocumentsTable
              documents={commodityCode.additionalReference}
              onChange={handleTableUpdate}
              createNewDocuments={true}
              disabled={disabled}
              declarationGroup={declaration?.generalInfo?.group}
            />
          </Box>

          <Divider my="lg" />
        </Stack>

        <Group justify="center" mt="xl">
          <Button data-pw="submit-code-btn" type="submit" variant="filled" size="lg">
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
